import React, {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
  useImperativeHandle,
  memo,
} from "react";
import { Container, Graphics } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { useAppActivity } from "../../../../Contexts/AppActivityContext";
import { PixiSettings } from "../../../../pixiSettings";
import { SOUND_NAME } from "../../../../sounds";
import anime from "animejs/lib/anime.es.js";
import FreeSpinIntroDialogController from "./FreeSpinIntroDialogController";
import TextButton from "../../../UserInterface/primary/Buttons/TextButton";
import { safeInit } from "../../../../utilities";
import { selectShowFreeSpinIntro } from "../../../../State/selectors";
import { soundManager } from "../../../../SoundManager";

// Overlay settings (matching WinAnimation exactly)
const OVERLAY_BACKGROUND_COLOR = 0x090f41;
const OVERLAY_BACKGROUND_ALPHA = 0.85;
const OVERLAY_FADE_DURATION = 350;

const FreeSpinIntroDialog = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const showFreeSpinIntro = useSelector(selectShowFreeSpinIntro);

  const introControllerRef = useRef(null);
  const introContainerRef = useRef(null);
  const overlayRef = useRef(null);
  const buttonRef = useRef(null);
  const dimensionsContext = useDimensions();
  const {
    dimensions,
    uiSymbolHeight: uiSymbolSize,
    device,
  } = dimensionsContext;
  const { isActive } = useAppActivity();

  // Overlay state (matching WinAnimation pattern)
  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const overlayAnimRef = useRef(null);

  // Expose fadeInBackground / fadeOutBackground via overlayRef (same API as WinAnimation)
  useImperativeHandle(overlayRef, () => ({
    fadeInBackground(callback) {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: 0 },
        alpha: OVERLAY_BACKGROUND_ALPHA,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          if (callback && typeof callback === "function") callback();
        },
      });
    },
    fadeOutBackground(callback) {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: OVERLAY_BACKGROUND_ALPHA },
        alpha: 0,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          setOverlayAlpha(0);
          if (callback && typeof callback === "function") callback();
        },
      });
    },
    setAlpha(alpha) {
      setOverlayAlpha(alpha);
    },
  }));

  // Determine button text based on device type
  const continueButtonKey = useMemo(() => {
    return device === PixiSettings.DEVICE.DESKTOP
      ? "click_to_continue"
      : "tap_to_continue";
  }, [device]);

  // Initialize controller once
  useEffect(() => {
    if (introContainerRef.current && !introControllerRef.current) {
      // Set button alpha to 0 for this instance
      if (buttonRef.current) {
        buttonRef.current.alpha = 0;
      }

      safeInit(() => {
        introControllerRef.current = new FreeSpinIntroDialogController(
          introContainerRef.current,
          dimensionsContext,
          dispatch,
          uiSymbolSize,
          overlayRef,
          buttonRef,
          t,
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle dimension changes
  useEffect(() => {
    if (introControllerRef.current) {
      introControllerRef.current.updateDimensions(
        dimensionsContext,
        uiSymbolSize,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiSymbolSize]);

  // Play animation when triggered
  useEffect(() => {
    if (introControllerRef.current && showFreeSpinIntro) {
      introControllerRef.current.startSequence();
    }
  }, [showFreeSpinIntro]);

  // Handle app activity changes - restart sequence when tab becomes active
  useEffect(() => {
    if (introControllerRef.current && showFreeSpinIntro) {
      if (isActive) {
        introControllerRef.current.restartSequence();
      }
    }
  }, [isActive, showFreeSpinIntro]);

  // Handle user click on overlay to close animation
  const handleBackgroundClick = useCallback(
    (evt) => {
      if (evt && typeof evt.preventDefault === "function") evt.preventDefault();
      if (evt && typeof evt.stopPropagation === "function")
        evt.stopPropagation();
      if (evt && typeof evt.stopImmediatePropagation === "function")
        evt.stopImmediatePropagation();

      if (introControllerRef.current && showFreeSpinIntro) {
        const accepted = introControllerRef.current.handleUserClick();
        if (accepted) {
          soundManager.play(SOUND_NAME.button_click);
        }
      }
    },
    [showFreeSpinIntro],
  );

  // Add spacebar handling
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) {
        return;
      }

      // Ignore auto-repeat
      if (event.repeat) {
        event.preventDefault();
        if (typeof event.stopPropagation === "function")
          event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function")
          event.stopImmediatePropagation();
        return;
      }

      if (!showFreeSpinIntro || !introControllerRef.current) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      try {
        const accepted = introControllerRef.current.handleUserClick();
        if (accepted) {
          soundManager.play(SOUND_NAME.button_click);
        }
      } catch (error) {
        if (
          introControllerRef.current &&
          introControllerRef.current.finishAnimation
        ) {
          introControllerRef.current.finishAnimation();
        }
      }
    };

    if (showFreeSpinIntro) {
      document.addEventListener("keydown", handleKeyDown, {
        capture: true,
        passive: false,
      });
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [showFreeSpinIntro, dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (introControllerRef.current) {
        introControllerRef.current.cleanup();
      }
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
    };
  }, []);

  // Memoize container position
  const containerPosition = useMemo(
    () => ({
      x: dimensions.width / 2,
      y: dimensions.height / 2,
    }),
    [dimensions.width, dimensions.height],
  );

  // Overlay draw function (matching WinAnimation)
  const drawOverlay = useCallback(
    (g) => {
      g.clear();
      g.beginFill(OVERLAY_BACKGROUND_COLOR);
      g.drawRect(0, 0, dimensions.width, dimensions.height);
      g.endFill();
    },
    [dimensions.width, dimensions.height],
  );

  return (
    <Container
      name="free_spin_intro"
      x={containerPosition.x}
      y={containerPosition.y}
      sortableChildren={true}
      zIndex={9}
      ref={introContainerRef}
    >
      {/* Background overlay - inline Graphics (WinAnimation pattern) */}
      <Graphics
        name="free_spin_intro_overlay"
        draw={drawOverlay}
        alpha={overlayAlpha}
        x={-containerPosition.x}
        y={-containerPosition.y}
        zIndex={0}
        eventMode={showFreeSpinIntro ? "static" : "none"}
        pointerdown={handleBackgroundClick}
        cursor={showFreeSpinIntro ? "pointer" : "default"}
      />

      {/* Spine animation and button managed internally by FreeSpinIntroDialogController */}

      <TextButton
        ref={buttonRef}
        y={containerPosition.y}
        translationKey={continueButtonKey}
      />
    </Container>
  );
};

export default memo(FreeSpinIntroDialog);
